import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useUsers, useResendInvite } from "@/api/queries/users";
import { useCurrentUser } from "@/api/queries/me";
import { formatDateTime, formatMinutes } from "@/lib/format";
import { roleLabel } from "@/lib/permissions";

export function PersonnelPage() {
  const [query, setQuery] = useState("");
  const me = useCurrentUser();
  const { data, isLoading } = useUsers({ q: query || undefined });
  const tz = me.data?.department?.timezone;
  const resendInvite = useResendInvite();
  const [resentId, setResentId] = useState<string | null>(null);

  const users = data ?? [];

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Personnel</h1>
          <p className="text-sm text-muted-foreground">
            Department roster and exposure totals.
          </p>
        </div>
        <Input
          placeholder="Search…"
          aria-label="Search personnel"
          className="w-64"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{users.length} people</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-6" />
              <Skeleton className="h-6" />
            </div>
          ) : users.length > 0 ? (
            <Table>
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Status</Th>
                  <Th>Sensor time</Th>
                  <Th>Last incident</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {users.map((u) => (
                  <Tr key={u.id}>
                    <Td className="font-medium">
                      <Link
                        to={`/personnel/${u.id}`}
                        className="text-primary hover:underline"
                      >
                        {u.display_name || u.email}
                      </Link>
                    </Td>
                    <Td>{u.email}</Td>
                    <Td>
                      {(u.roles.length > 0 ? u.roles : [u.role])
                        .map(roleLabel)
                        .join(", ")}
                    </Td>
                    <Td>
                      <Badge
                        variant={
                          u.invite_state === "pending"
                            ? "secondary"
                            : "success"
                        }
                      >
                        {u.invite_state === "pending" ? "Pending" : "Active"}
                      </Badge>
                    </Td>
                    <Td>{formatMinutes(u.sensor_minutes_recorded)}</Td>
                    <Td>{formatDateTime(u.last_incident_at ?? null, tz)}</Td>
                    <Td>
                      {u.invite_state === "pending" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={resendInvite.isPending}
                          onClick={() => {
                            resendInvite.mutate(u.id, {
                              onSuccess: () => setResentId(u.id),
                            });
                          }}
                        >
                          {resendInvite.isPending && resentId === u.id
                            ? "Sending…"
                            : resentId === u.id
                              ? "Sent"
                              : "Reinvite"}
                        </Button>
                      ) : null}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">
              No personnel match this filter.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
